import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import ObservationMediaFile from "./ObservationMediaFile";
import Paper from "../../ui/Paper";
import { fadeIn } from "../../ui/animations";
import { API_BASE_URL } from "../../app/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { selectAuth } from "../iam/slice";
import semver from "semver";

const Container = styled(Paper)`
  padding: 0.5rem;
  animation: 1s ${fadeIn} ease-out;
  display: flex;
  align-items: stretch;
  justify-content: center;
  grid-column: ${(props) =>
		props.file?.name === "events_and_tles_widget" ? "1 / 3" : "auto"};
  min-height: ${(props) =>
		props.file?.name === "events_and_tles_widget" ? "45rem" : "unset"};
`;

const FileTable = styled.table`
	width: 100%;
`;

const FileTd = styled.td`
		padding: 0.5rem;
	`;

// TODO: In `Container` solve layout problem where nested `iframes` do not
// stretch to fill the full available space, despite being set with "height: 100%"
// such as in the Events and TLEs HTML widget.

/* For a given file ID, specifies a weight for ordering.
 * Smaller weights display first, larger weights display last.
 * Keys correspond to `id` in observation media files.
 */
const displayOrderWeightByID = {
	wiggle: 1,
	star_map: 2,
	points_3d_widget: 3,
	allsky: 4,
	event_rate_plot: 5,
	labelled_events_plot: 6,
	colourtime: 7,
	video_preview: 8,
	// Anything not in here has a default, high weight (displays last)
};
const byDisplayWeight = (a, b) => {
	if (!a || !a.name || !b || !b.name) {
		return 0;
	}
	return (
		(displayOrderWeightByID[a.name] || Number.POSITIVE_INFINITY) -
		(displayOrderWeightByID[b.name] || Number.POSITIVE_INFINITY)
	);
};

// Check if the file type is an image or video which can be rendered
const isMedia = ({ type }) =>
	type?.includes("image/") ||
	type?.includes("video/") ||
	type?.includes("text/html");

// Where is my RamdaJS?
const not = (fn) => (o) => !fn(o);

/**
 * From a list of files get the latest version
 * e.g. from [{"id":"star_map_v1.2.0"},{"id":"star_map_v2.0.0"}, "id":"wiggle_v1.2.0"] we should get
 * [{"id":"star_map_v2.0.0"}, "id":"wiggle_v1.2.0"]
 * @param derivedFiles
 * @returns {*}
 */
function getLatestVersions(derivedFiles = []) {
	const latestVersions = {};
	const filesByID = {};
	derivedFiles.forEach((item) => {
		if (item?.id) {
			const [name, version] = item.id.split("_v");
			if (name && version) {
				// store the base file name without the version as name
				filesByID[item.id] = { name, ...item };
				if (!latestVersions[name] || semver.gt(version, latestVersions[name])) {
					latestVersions[name] = version;
				}
			}
		}
	});

	return Object.entries(latestVersions).map(
		([name, version]) => filesByID[`${name}_v${version}`],
	);
}

/**
 * Renders a list of the derived files from an observation
 * typically images or videos but can be JSON etc.
 * The media files are render while the other files just have their detials listed
 * @param derivedFiles The files to be rendered
 * @param observationId
 * @returns {JSX.Element}
 * @constructor
 */
const ObservationFiles = ({
	rawFiles = [],
	derivedFiles = [],
	observationId,
}) => {
	// filter only latest versions of the derived files
	const uniqueDerivedFiles = getLatestVersions(derivedFiles);

	const mediaFiles = uniqueDerivedFiles
		.slice()
		.filter(isMedia)
		.sort(byDisplayWeight);
	const nonMediaFiles = uniqueDerivedFiles.slice().filter(not(isMedia));

	return (
		<>
			{mediaFiles.map((file, i) => (
				<Container key={i} file={file}>
					<ObservationMediaFile
						observationId={observationId}
						file={file}
						expanded={true}
					/>
				</Container>
			))}
			{/*  list any non-media files*/}
			{nonMediaFiles && nonMediaFiles.length > 0 && (
				<div>
					<h3>Derived files</h3>
					<Container>
						<NonMediaFiles
							observationId={observationId}
							files={nonMediaFiles}
						/>
					</Container>
				</div>
			)}
			{rawFiles && rawFiles.length > 0 && (
				<div>
					<h3>Raw data files</h3>
					<Container>
						<RawFiles observationId={observationId} files={rawFiles} />
					</Container>
				</div>
			)}
		</>
	);
};

// render details about any raw files (preprocessing)
const RawFiles = ({ observationId, files = [] }) => {
	const { token } = useSelector(selectAuth);

	return (
		<FileTable>
			<thead>
				<tr>
					<th>Type</th>
					<th />
				</tr>
			</thead>
			<tbody>
				{files.map((file) => (
					<tr key={file.id}>
						<FileTd>{file.type}</FileTd>
						<FileTd>
							<a
								href={`${API_BASE_URL}/api/observations/${observationId}/raw_file/${
									file.id
								}?download=true${token ? "&token=" + encodeURIComponent(token) : ""}`}
								download={file?.download_name}
							>
								<FontAwesomeIcon icon={faDownload} />
							</a>
						</FileTd>
					</tr>
				))}
			</tbody>
		</FileTable>
	);
};

RawFiles.propTypes = {
	observationId: PropTypes.string,
	files: PropTypes.array,
};

// render details about any files that are displayable media
const NonMediaFiles = ({ observationId, files = [] }) => {
	const { token } = useSelector(selectAuth);

	return (
		<FileTable>
			<thead>
				<tr>
					<th>Name</th>
					<th>Type</th>
					<th />
				</tr>
			</thead>
			<tbody>
				{files.map((file) => (
					<tr key={file.uri || file.display_name}>
						<FileTd>{file.display_name}</FileTd>
						<FileTd>{file.type}</FileTd>
						<FileTd>
							<a
								href={`${API_BASE_URL}/api/observations/${observationId}/${
									file.id
								}?download=true${
									token ? "&token=" + encodeURIComponent(token) : ""
								}`}
								download={file.display_name}
							>
								<FontAwesomeIcon icon={faDownload} />
							</a>
						</FileTd>
					</tr>
				))}
			</tbody>
		</FileTable>
	);
};

NonMediaFiles.propTypes = {
	observationId: PropTypes.string,
	files: PropTypes.array,
};

ObservationFiles.propTypes = {
	observationId: PropTypes.string,
	derivedFiles: PropTypes.array, // The derived files to be rendered
	rawFiles: PropTypes.array, // The raw files
};

export default ObservationFiles;
